import { useState, useRef, useEffect } from 'react';
import {
  VideoAnalysisService,
  VideoAnalysisTriggerRequest,
} from 'src/services/video-analysis/video-analysis.service';
import { useToast } from 'src/components/ui/use-toast';
import { Logger } from 'src/utils/logger';

interface UseVideoAnalysisReturn {
  generateContent: (videoUrl: string, contentType: VideoAnalysisTriggerRequest['request_type']) => Promise<string>;
  isGenerating: boolean;
  cancel: () => void;
}

export const useVideoAnalysis = (): UseVideoAnalysisReturn => {
  const [isGenerating, setIsGenerating] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { toast } = useToast();

  // Cleanup function to abort ongoing operations
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const generateContent = async (
    videoUrl: string,
    contentType: VideoAnalysisTriggerRequest['request_type']
  ): Promise<string> => {
    // Create new abort controller for this operation
    abortControllerRef.current = new AbortController();
    const abortController = abortControllerRef.current;

    setIsGenerating(true);

    try {
      const requestId = VideoAnalysisService.generateRequestId(contentType);

      // Check if operation was cancelled before starting
      if (abortController.signal.aborted) {
        return '';
      }

      // Show initial toast to indicate generation started
      toast({
        description: `Generating ${VideoAnalysisService.getRequestTypeLabel(contentType)}...`,
        variant: 'info',
      });

      // Trigger analysis
      await VideoAnalysisService.triggerAnalysis({
        video_url: videoUrl,
        request_type: contentType,
        request_id: requestId,
        force: true, // Always force fresh content generation
      });

      // Poll for results with timeout
      const maxAttempts = 30; // 30 seconds max
      let attempts = 0;

      while (attempts < maxAttempts) {
        // Check if operation was cancelled
        if (abortController.signal.aborted) {
          return '';
        }

        await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second
        attempts++;

        // Check again after timeout in case it was cancelled during sleep
        if (abortController.signal.aborted) {
          return '';
        }

        try {
          const result = await VideoAnalysisService.getResult({
            request_id: requestId,
            request_type: contentType,
          });

          // Final check before processing result
          if (abortController.signal.aborted) {
            return '';
          }

          if (result.status === 'completed' && result.description) {
            toast({
              description: `${VideoAnalysisService.getRequestTypeLabel(contentType)} generated successfully!`,
              variant: 'success',
            });
            return result.description;
          } else if (result.status === 'failed') {
            throw new Error(result.error || 'Generation failed');
          }
          // Continue polling if status is 'pending' or 'processing'
        } catch (error) {
          Logger.warn('Polling attempt failed, continuing', 'useVideoAnalysis', error);
          // Continue polling on API errors
        }
      }

      // Timeout reached
      throw new Error('Generation timeout - please try again');
    } catch (error: any) {
      // Don't show error if operation was cancelled
      if (abortController.signal.aborted) {
        return '';
      }
      
      Logger.error('Content generation failed', 'useVideoAnalysis', error);
      toast({
        description: error?.message || 'Failed to generate content. Please try again.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      // Always reset generating state to re-enable buttons
      setIsGenerating(false);
    }
  };

  const cancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsGenerating(false);
  };

  return { generateContent, isGenerating, cancel };
};
